import {
  TaskOutcomeFieldsSchema,
  type TaskOutcomeField,
} from '@erp/shared-types';
import {
  parseTaskActionFormSchema,
  SYSTEM_ENTITY_LABELS,
  SystemEntity,
  TASK_ACTION_FORM_FIELD_TYPES,
  TERMINAL_TASK_STATUSES,
  TaskTypeActionsSchema,
  TaskProcedureBindingsSchema,
  type TaskActionFormField,
  type TaskActionFormFieldType,
  type TaskCompletionMode,
  type TaskExpectedSubtask,
  type TaskProcedureBinding,
  type TaskProcedurePhase,
  type TaskTypeAction,
  type TerminalTaskStatus,
  type TaskTypeDefaultAssigneeAllocation,
  type TaskTypeDefaultAssigneeInput,
  type TaskTypeDefaultAssigneeType,
  type TaskTypeEscalationTargetInput,
  type TaskTypeEscalationTargetType,
  type TaskTypeMetric,
  type TaskTypeView,
} from '@erp/shared-types';
import type { TaskTypeResponse } from '@erp/rpc';
import { viewForSave, viewTextFromStored } from './taskTypeViewDraft';
import {
  metricDraftsFromStored,
  metricDraftsSignature,
  metricsForSave,
  type TaskTypeMetricDraft,
} from './taskTypeMetricsDraft';
import {
  formatNamespaceLabel,
  isValidNamespaceKey,
} from 'src/components/namespaces/namespaceLabels';

// Pure validation logic for the task type admin card, kept free of React so
// it stays trivially unit-testable.

/** Lowercase kebab-case, mirroring the taskTypes contract's key rule. */
export const TASK_TYPE_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isValidTaskTypeKey(key: string): boolean {
  const trimmed = key.trim();
  return (
    trimmed.length > 0 &&
    trimmed.length <= 100 &&
    TASK_TYPE_KEY_PATTERN.test(trimmed)
  );
}

/** Suggested machine key from a human name (empty when nothing slugifies). */
export function taskTypeKeyFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100)
    .replace(/-+$/, '');
}

/**
 * Validates against the namespaces contract's own key schema
 * (alphanumeric, 1-10 chars; the server uppercases on save).
 */
export const isValidTaskNamespaceKey = isValidNamespaceKey;

/**
 * Localized "Name (KEY)" label for a namespace, shared by the default
 * namespace picker and the types table so translators can reorder the parts.
 */
export const formatTaskNamespaceLabel = formatNamespaceLabel;

export type ActionDraftErrorCode =
  | 'key_required'
  | 'key_invalid'
  | 'key_duplicate'
  | 'label_required'
  | 'event_key_required'
  | 'workflow_template_key_required'
  | 'outcome_required'
  | 'outcome_reason_required'
  | 'schema_invalid';

export interface ActionDraftError {
  code: ActionDraftErrorCode;
  /** 0-based draft index; for schema errors this is the first failing action. */
  index: number;
  key?: string;
  detail?: string;
}

export type ParsedTaskTypeActions =
  | { ok: true; actions: TaskTypeAction[] }
  | { ok: false; error: ActionDraftError };

export type ParsedTaskTypeActionsJson =
  { ok: true; actions: TaskTypeAction[] } | { ok: false; error: string };

/** Draft row for one field inside an action's optional formSchema. */
export interface ActionFormFieldDraft {
  clientId: string;
  key: string;
  label: string;
  type: TaskActionFormFieldType;
  required: boolean;
  helpText: string;
  /** Comma- or newline-separated options for `select` fields. */
  optionsText: string;
}

/**
 * Draft row for the structured actions editor. Maps 1:1 onto the stored
 * TaskTypeAction effect union. Form fields are edited as structured drafts
 * and serialized to `formSchema` on save.
 */
export type TaskTypeActionEffectKind = TaskTypeAction['effect']['kind'];

export interface TaskTypeActionDraft {
  /** Stable client id so React keys survive key renames. */
  clientId: string;
  key: string;
  label: string;
  effectKind: TaskTypeActionEffectKind;
  /** eventKey or workflowTemplateKey depending on effectKind. */
  effectTarget: string;
  /** Terminal status written by a `set_outcome` effect. */
  outcomeStatus: TerminalTaskStatus;
  /** Outcome key written by a `set_outcome` effect. Ignored when cancelling. */
  outcome: string;
  /** Static outcome reason. A form field named `outcomeReason` overrides it. */
  outcomeReason: string;
  formFields: ActionFormFieldDraft[];
}

let draftIdCounter = 0;
let formFieldDraftIdCounter = 0;

export function newActionDraft(
  partial?: Partial<Omit<TaskTypeActionDraft, 'clientId'>>,
): TaskTypeActionDraft {
  draftIdCounter += 1;
  return {
    clientId: `action-draft-${draftIdCounter}`,
    key: '',
    label: '',
    effectKind: 'emit_event',
    effectTarget: '',
    outcomeStatus: 'completed',
    outcome: '',
    outcomeReason: '',
    formFields: [],
    ...partial,
  };
}

/**
 * Terminal statuses the drafted actions can close a task as. A task type with
 * custom actions hides the default Complete / Fail / Cancel controls, so a
 * status missing here cannot be reached from the task page.
 */
export function closeStatusesCoveredByActions(
  drafts: readonly TaskTypeActionDraft[],
): TerminalTaskStatus[] {
  return TERMINAL_TASK_STATUSES.filter((status) =>
    drafts.some(
      (draft) =>
        draft.effectKind === 'set_outcome' && draft.outcomeStatus === status,
    ),
  );
}

const CLOSE_PRESET_KEYS: Record<TerminalTaskStatus, string> = {
  completed: 'complete',
  failed: 'fail',
  cancelled: 'cancel',
};

function unusedActionKey(
  base: string,
  drafts: readonly TaskTypeActionDraft[],
): string {
  const taken = new Set(drafts.map((draft) => draft.key.trim()));
  let key = base;
  for (let n = 2; taken.has(key); n += 1) key = `${base}-${n}`;
  return key;
}

function presetOutcome(
  status: TerminalTaskStatus,
  outcomeChoices: readonly string[],
): string {
  // Only the default vocabulary's outcomes are safe guesses; with a custom
  // vocabulary the admin picks, so a preset never records the wrong outcome.
  const preferred = status === 'completed' ? 'success' : 'abandoned';
  return outcomeChoices.includes(preferred) ? preferred : '';
}

/**
 * A ready-to-save action that closes the task as `status`, standing in for
 * the matching default control. Cancellation asks for a required reason
 * because the server records only a reason when cancelling.
 */
export function closeActionPreset({
  status,
  label,
  reasonLabel,
  outcomeChoices,
  existing,
}: {
  status: TerminalTaskStatus;
  label: string;
  reasonLabel: string;
  outcomeChoices: readonly string[];
  existing: readonly TaskTypeActionDraft[];
}): TaskTypeActionDraft {
  const cancelling = status === 'cancelled';
  return newActionDraft({
    key: unusedActionKey(CLOSE_PRESET_KEYS[status], existing),
    label,
    effectKind: 'set_outcome',
    outcomeStatus: status,
    outcome: cancelling ? '' : presetOutcome(status, outcomeChoices),
    formFields: cancelling
      ? [
          newActionFormFieldDraft({
            key: 'outcomeReason',
            label: reasonLabel,
            type: 'textarea',
            required: true,
          }),
        ]
      : [],
  });
}

export function newActionFormFieldDraft(
  partial?: Partial<Omit<ActionFormFieldDraft, 'clientId'>>,
): ActionFormFieldDraft {
  formFieldDraftIdCounter += 1;
  return {
    clientId: `action-form-field-${formFieldDraftIdCounter}`,
    key: '',
    label: '',
    type: 'text',
    required: false,
    helpText: '',
    optionsText: '',
    ...partial,
  };
}

export function actionFormFieldDraftsFromSchema(
  formSchema: TaskTypeAction['formSchema'] | undefined,
): ActionFormFieldDraft[] {
  const parsed = parseTaskActionFormSchema(formSchema);
  if (!parsed.ok || !parsed.fields || parsed.fields.length === 0) {
    return [];
  }
  return parsed.fields.map((field) =>
    newActionFormFieldDraft({
      key: field.key,
      label: field.label,
      type: field.type,
      required: field.required,
      helpText: field.helpText ?? '',
      optionsText: field.options?.join(', ') ?? '',
    }),
  );
}

function parseOptionsText(optionsText: string): string[] {
  return optionsText
    .split(/[\n,]/)
    .map((option) => option.trim())
    .filter((option) => option.length > 0);
}

/**
 * Build a wire formSchema from field drafts. Empty field list clears the
 * schema (action runs with no form). Incomplete rows still produce a schema
 * so {@link parseActionDrafts} can surface schema_invalid via Zod.
 */
export function formSchemaFromFieldDrafts(
  fields: readonly ActionFormFieldDraft[],
): TaskTypeAction['formSchema'] | undefined {
  if (fields.length === 0) return undefined;

  const wireFields: TaskActionFormField[] = fields.map((field) => {
    const wire: TaskActionFormField = {
      key: field.key.trim(),
      label: field.label.trim(),
      type: field.type,
      required: field.required,
    };
    const helpText = field.helpText.trim();
    if (helpText) {
      wire.helpText = helpText;
    }
    if (field.type === 'select') {
      wire.options = parseOptionsText(field.optionsText);
    }
    return wire;
  });

  return { fields: wireFields };
}

export const ACTION_FORM_FIELD_TYPE_OPTIONS: readonly TaskActionFormFieldType[] =
  TASK_ACTION_FORM_FIELD_TYPES;

function effectDraftFromStored(
  effect: TaskTypeAction['effect'],
): Pick<
  TaskTypeActionDraft,
  'effectKind' | 'effectTarget' | 'outcomeStatus' | 'outcome' | 'outcomeReason'
> {
  switch (effect.kind) {
    case 'emit_event':
      return {
        effectKind: 'emit_event',
        effectTarget: effect.eventKey,
        outcomeStatus: 'completed',
        outcome: '',
        outcomeReason: '',
      };
    case 'start_workflow':
      return {
        effectKind: 'start_workflow',
        effectTarget: effect.workflowTemplateKey,
        outcomeStatus: 'completed',
        outcome: '',
        outcomeReason: '',
      };
    case 'set_outcome':
      return {
        effectKind: 'set_outcome',
        effectTarget: '',
        outcomeStatus: effect.status,
        outcome: effect.outcome ?? '',
        outcomeReason: effect.outcomeReason ?? '',
      };
  }
}

export function actionDraftsFromStored(
  actions: readonly TaskTypeAction[] | null | undefined,
): TaskTypeActionDraft[] {
  if (!actions || actions.length === 0) return [];
  return actions.map((action) =>
    newActionDraft({
      key: action.key,
      label: action.label,
      ...effectDraftFromStored(action.effect),
      formFields: actionFormFieldDraftsFromSchema(action.formSchema),
    }),
  );
}

const TASK_ACTION_OUTCOME_REASON_KEY = 'outcomeReason';

type EffectDraftResult =
  | { ok: true; effect: TaskTypeAction['effect'] }
  | { ok: false; error: ActionDraftError };

function effectFromDraft(
  draft: TaskTypeActionDraft,
  index: number,
): EffectDraftResult {
  if (draft.effectKind === 'emit_event') {
    return targetEffectFromDraft(draft, index, 'event_key_required');
  }
  if (draft.effectKind === 'start_workflow') {
    return targetEffectFromDraft(
      draft,
      index,
      'workflow_template_key_required',
    );
  }
  return outcomeEffectFromDraft(draft, index);
}

function targetEffectFromDraft(
  draft: TaskTypeActionDraft,
  index: number,
  missingCode: 'event_key_required' | 'workflow_template_key_required',
): EffectDraftResult {
  const target = draft.effectTarget.trim();
  if (!target) return { ok: false, error: { code: missingCode, index } };
  if (draft.effectKind === 'emit_event') {
    return { ok: true, effect: { kind: 'emit_event', eventKey: target } };
  }
  return {
    ok: true,
    effect: { kind: 'start_workflow', workflowTemplateKey: target },
  };
}

function trimmedOutcomeReason(draft: TaskTypeActionDraft): string | undefined {
  const outcomeReason = draft.outcomeReason.trim();
  return outcomeReason.length > 0 ? outcomeReason : undefined;
}

function draftHasOutcomeReasonField(draft: TaskTypeActionDraft): boolean {
  return draft.formFields.some(
    (field) => field.key.trim() === TASK_ACTION_OUTCOME_REASON_KEY,
  );
}

function cancelledOutcomeEffect(
  draft: TaskTypeActionDraft,
  index: number,
  outcomeReason: string | undefined,
): EffectDraftResult {
  if (outcomeReason) {
    return {
      ok: true,
      effect: { kind: 'set_outcome', status: 'cancelled', outcomeReason },
    };
  }
  if (draftHasOutcomeReasonField(draft)) {
    return { ok: true, effect: { kind: 'set_outcome', status: 'cancelled' } };
  }
  return { ok: false, error: { code: 'outcome_reason_required', index } };
}

function closingOutcomeEffect(
  draft: TaskTypeActionDraft,
  index: number,
  outcomeReason: string | undefined,
): EffectDraftResult {
  const outcome = draft.outcome.trim();
  if (!outcome) {
    return { ok: false, error: { code: 'outcome_required', index } };
  }
  return {
    ok: true,
    effect: {
      kind: 'set_outcome',
      status: draft.outcomeStatus,
      outcome,
      ...(outcomeReason ? { outcomeReason } : {}),
    },
  };
}

function outcomeEffectFromDraft(
  draft: TaskTypeActionDraft,
  index: number,
): EffectDraftResult {
  const outcomeReason = trimmedOutcomeReason(draft);
  if (draft.outcomeStatus === 'cancelled') {
    return cancelledOutcomeEffect(draft, index, outcomeReason);
  }
  return closingOutcomeEffect(draft, index, outcomeReason);
}

/**
 * Validate drafts and produce the wire TaskTypeAction[]. Empty drafts list
 * means "no actions". Incomplete rows fail with a human-readable error.
 */
function draftToAction(
  draft: TaskTypeActionDraft,
  index: number,
  seenKeys: Set<string>,
):
  | { ok: true; action: TaskTypeAction }
  | { ok: false; error: ActionDraftError } {
  const key = draft.key.trim();
  const label = draft.label.trim();

  if (!key) {
    return { ok: false, error: { code: 'key_required', index } };
  }
  if (!isValidTaskTypeKey(key)) {
    return { ok: false, error: { code: 'key_invalid', index } };
  }
  if (seenKeys.has(key)) {
    return { ok: false, error: { code: 'key_duplicate', index, key } };
  }
  if (!label) {
    return { ok: false, error: { code: 'label_required', index } };
  }

  const effect = effectFromDraft(draft, index);
  if (!effect.ok) return effect;

  const action: TaskTypeAction = {
    key,
    label,
    effect: effect.effect,
  };
  const formSchema = formSchemaFromFieldDrafts(draft.formFields);
  if (formSchema !== undefined) {
    action.formSchema = formSchema;
  }
  return { ok: true, action };
}

function validateParsedActions(
  actions: TaskTypeAction[],
): ParsedTaskTypeActions {
  const validated = TaskTypeActionsSchema.safeParse(actions);
  if (validated.success) {
    return { ok: true, actions: validated.data };
  }
  const issue = validated.error.issues[0];
  const path = issue?.path.length ? `${issue.path.join('.')}: ` : '';
  const index = typeof issue?.path[0] === 'number' ? issue.path[0] : 0;
  return {
    ok: false,
    error: {
      code: 'schema_invalid',
      index,
      detail: `${path}${issue?.message ?? 'Invalid actions'}`,
    },
  };
}

export function parseActionDrafts(
  drafts: readonly TaskTypeActionDraft[],
): ParsedTaskTypeActions {
  if (drafts.length === 0) return { ok: true, actions: [] };

  const actions: TaskTypeAction[] = [];
  const seenKeys = new Set<string>();

  for (const [index, draft] of drafts.entries()) {
    const parsed = draftToAction(draft, index, seenKeys);
    if (!parsed.ok) return parsed;
    seenKeys.add(parsed.action.key);
    actions.push(parsed.action);
  }

  return validateParsedActions(actions);
}

/** Anchor entity options sorted by human label, for the searchable picker. */
export function sortedAnchorEntityOptions(): Array<{
  id: SystemEntity;
  label: string;
}> {
  return Object.values(SystemEntity)
    .map((id) => ({ id, label: SYSTEM_ENTITY_LABELS[id] }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Kept for unit tests covering the legacy JSON validation path. */
export function parseTaskTypeActionsJson(
  text: string,
): ParsedTaskTypeActionsJson {
  const trimmed = text.trim();
  if (!trimmed) return { ok: true, actions: [] };

  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Invalid JSON',
    };
  }

  const parsed = TaskTypeActionsSchema.safeParse(raw);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
    return { ok: false, error: `${path}${issue.message}` };
  }
  return { ok: true, actions: parsed.data };
}

/** Kept for unit tests covering the legacy JSON validation path. */
export function stringifyTaskTypeActions(
  actions: readonly TaskTypeAction[] | undefined,
): string {
  if (!actions || actions.length === 0) return '';
  return JSON.stringify(actions, null, 2);
}

/** One SOP parameter binding row: `param` -> a `$.task.*` path or literal. */
export interface TaskTypeProcedureParamDraft {
  clientId: string;
  param: string;
  value: string;
}

/**
 * Draft row for the playbook (procedures) editor. Maps 1:1 onto a stored
 * `TaskProcedureBinding`; `excluded` never appears at type scope.
 */
export interface TaskTypeProcedureDraft {
  /** Stable client id so React keys survive SOP re-selection. */
  clientId: string;
  sopId: string;
  mode: 'required' | 'suggested';
  phase: TaskProcedurePhase;
  whenToUse: string;
  /** Null follows the SOP's published version. */
  pinnedVersionId: string | null;
  params: TaskTypeProcedureParamDraft[];
}

let procedureDraftIdCounter = 0;
let procedureParamDraftIdCounter = 0;

export function newProcedureDraft(
  partial?: Partial<Omit<TaskTypeProcedureDraft, 'clientId'>>,
): TaskTypeProcedureDraft {
  procedureDraftIdCounter += 1;
  return {
    clientId: `procedure-draft-${procedureDraftIdCounter}`,
    sopId: '',
    mode: 'required',
    phase: 'main',
    whenToUse: '',
    pinnedVersionId: null,
    params: [],
    ...partial,
  };
}

export function newProcedureParamDraft(
  partial?: Partial<Omit<TaskTypeProcedureParamDraft, 'clientId'>>,
): TaskTypeProcedureParamDraft {
  procedureParamDraftIdCounter += 1;
  return {
    clientId: `procedure-param-draft-${procedureParamDraftIdCounter}`,
    param: '',
    value: '',
    ...partial,
  };
}

export function procedureDraftsFromStored(
  bindings: readonly TaskProcedureBinding[] | null | undefined,
): TaskTypeProcedureDraft[] {
  return (bindings ?? []).flatMap((b) =>
    b.mode === 'excluded'
      ? []
      : [
          newProcedureDraft({
            sopId: b.sopId,
            mode: b.mode === 'suggested' ? 'suggested' : 'required',
            phase: b.phase,
            whenToUse: b.whenToUse ?? '',
            pinnedVersionId: b.pinnedVersionId,
            params: Object.entries(b.parameterBindings).map(([param, value]) =>
              newProcedureParamDraft({ param, value }),
            ),
          }),
        ],
  );
}

/** Max length of the one-line disclosure hint (mirrors the shared schema). */
export const PROCEDURE_WHEN_TO_USE_MAX = 280;

export type ProcedureDraftErrorCode =
  | 'missing_sop'
  | 'duplicate_sop'
  | 'duplicate_param'
  | 'empty_param'
  | 'hint_too_long'
  | 'too_many_procedures'
  | 'invalid_binding';

export type ParsedTaskTypeProcedures =
  | { ok: true; procedures: TaskProcedureBinding[] }
  | { ok: false; error: { code: ProcedureDraftErrorCode; index: number } };

/** The parameter rows of one draft as stored bindings, or the first row error. */
function parseProcedureParams(
  params: readonly TaskTypeProcedureParamDraft[],
):
  | { ok: true; bindings: Record<string, string> }
  | { ok: false; code: ProcedureDraftErrorCode } {
  const bindings: Record<string, string> = {};
  for (const row of params) {
    const param = row.param.trim();
    if (!param) return { ok: false, code: 'empty_param' };
    if (param in bindings) return { ok: false, code: 'duplicate_param' };
    bindings[param] = row.value;
  }
  return { ok: true, bindings };
}

/** Validate + serialize procedure drafts to the stored binding shape. */
export function parseProcedureDrafts(
  drafts: readonly TaskTypeProcedureDraft[],
): ParsedTaskTypeProcedures {
  const procedures: TaskProcedureBinding[] = [];
  const seenSops = new Set<string>();
  for (const [index, draft] of drafts.entries()) {
    if (!draft.sopId) {
      return { ok: false, error: { code: 'missing_sop', index } };
    }
    if (seenSops.has(draft.sopId)) {
      return { ok: false, error: { code: 'duplicate_sop', index } };
    }
    seenSops.add(draft.sopId);
    const whenToUse = draft.whenToUse.trim();
    if (whenToUse.length > PROCEDURE_WHEN_TO_USE_MAX) {
      return { ok: false, error: { code: 'hint_too_long', index } };
    }
    const params = parseProcedureParams(draft.params);
    if (!params.ok) return { ok: false, error: { code: params.code, index } };
    procedures.push({
      sopId: draft.sopId,
      mode: draft.mode,
      // Phase only means something for a prescribed procedure.
      phase: draft.mode === 'required' ? draft.phase : 'main',
      whenToUse: whenToUse || null,
      pinnedVersionId: draft.pinnedVersionId,
      parameterBindings: params.bindings,
    });
  }
  return validateProcedureBindings(procedures);
}

function validateProcedureBindings(
  procedures: TaskProcedureBinding[],
): ParsedTaskTypeProcedures {
  const parsed = TaskProcedureBindingsSchema.safeParse(procedures);
  if (parsed.success) return { ok: true, procedures: parsed.data };
  const issue = parsed.error.issues[0];
  const index = issue?.path[0];
  return {
    ok: false,
    error: {
      code:
        issue?.code === 'too_big' && issue.path.length === 0
          ? 'too_many_procedures'
          : 'invalid_binding',
      index: typeof index === 'number' ? index : 0,
    },
  };
}

/** Client-id-free projection of the drafts, for dirty comparison. */
export function procedureDraftSignature(
  drafts: readonly TaskTypeProcedureDraft[],
): string {
  return JSON.stringify(
    drafts.map(
      ({ sopId, mode, phase, whenToUse, pinnedVersionId, params }) => ({
        sopId,
        mode,
        phase,
        whenToUse,
        pinnedVersionId,
        params: params.map(({ param, value }) => ({ param, value })),
      }),
    ),
  );
}

export const NO_ANCHOR_VALUE = '__none__';
/** Sentinel: leave outcomeEnumId null and use DEFAULT_TASK_OUTCOMES. */
export const NO_OUTCOME_ENUM_VALUE = '__default_outcomes__';
export const NO_INTENDED_ASSIGNEE_VALUE = '__unset__';
/** Sentinel: no default namespace — tasks fall back to the org default. */
export const NO_NAMESPACE_VALUE = '__org_default__';
/** Sentinel: type accepts no typed create-time input. */
export const NO_INPUT_SCHEMA_VALUE = '__no_input_schema__';

/** Default assignee kind selected in the editor; `none` = no default. */
export type TaskTypeDefaultAssigneeMode = TaskTypeDefaultAssigneeType | 'none';

/** Default-assignee slice of the editor state. */
export type TaskTypeDefaultAssigneeEditorState = Pick<
  TaskTypeEditorState,
  | 'defaultAssigneeMode'
  | 'defaultAssigneeUserId'
  | 'defaultAssigneeAgentDefinitionId'
  | 'defaultAssigneeUserGroupId'
  | 'defaultAssigneeWorkflowId'
  | 'defaultAssigneeAllocation'
>;

/** Wire payload for the default assignee: every column, always sent together. */
export type TaskTypeDefaultAssigneePayload = Required<{
  [K in keyof TaskTypeDefaultAssigneeInput]: NonNullable<
    TaskTypeDefaultAssigneeInput[K]
  > | null;
}>;

/** Escalation target kind selected in the editor; `none` = no target. */
export type TaskTypeEscalationTargetMode =
  TaskTypeEscalationTargetType | 'none';

/** Escalation-target slice of the editor state. */
export type TaskTypeEscalationTargetEditorState = Pick<
  TaskTypeEditorState,
  | 'escalationTargetMode'
  | 'escalationTargetUserId'
  | 'escalationTargetUserGroupId'
  | 'escalationTargetAllocation'
>;

/** Wire payload for the escalation target: every column, always sent together. */
export type TaskTypeEscalationTargetPayload = Required<{
  [K in keyof TaskTypeEscalationTargetInput]: NonNullable<
    TaskTypeEscalationTargetInput[K]
  > | null;
}>;

export interface TaskTypeEditorState {
  /** Null = creating a new type; set = editing that type. */
  editing: TaskTypeResponse | null;
  key: string;
  name: string;
  description: string;
  anchorEntityType: string;
  expectedSubtasks: TaskExpectedSubtask[];
  completionMode: TaskCompletionMode;
  /** NO_OUTCOME_ENUM_VALUE = platform defaults; otherwise a Core Enum id. */
  outcomeEnumId: string;
  outcomeFields: TaskOutcomeField[];
  actions: TaskTypeActionDraft[];
  /** The type's playbook: SOP bindings (required / suggested). */
  procedures: TaskTypeProcedureDraft[];
  /** NO_NAMESPACE_VALUE = org default; otherwise a task namespace id. */
  taskNamespaceId: string;
  /** `none` = tasks are created unassigned unless the caller names one. */
  defaultAssigneeMode: TaskTypeDefaultAssigneeMode;
  /** Selected user id when mode is `user`; empty = not yet picked. */
  defaultAssigneeUserId: string;
  /** Selected agent definition id when mode is `agent`; empty = not yet picked. */
  defaultAssigneeAgentDefinitionId: string;
  /** Selected user group id when mode is `user_group`; empty = not yet picked. */
  defaultAssigneeUserGroupId: string;
  /** Selected workflow (action) id when mode is `workflow`; empty = not yet picked. */
  defaultAssigneeWorkflowId: string;
  /** How a `user_group` default picks a member per task; empty = not yet picked. */
  defaultAssigneeAllocation: TaskTypeDefaultAssigneeAllocation | '';
  /** `none` = agent hand-offs follow the creator / workflow / watcher cascade. */
  escalationTargetMode: TaskTypeEscalationTargetMode;
  /** Selected user id when mode is `user`; empty = not yet picked. */
  escalationTargetUserId: string;
  /** Selected user group id when mode is `user_group`; empty = not yet picked. */
  escalationTargetUserGroupId: string;
  /** How a `user_group` target picks a member per escalation; empty = not yet picked. */
  escalationTargetAllocation: TaskTypeDefaultAssigneeAllocation | '';
  /** NO_INPUT_SCHEMA_VALUE = no create-time contract; otherwise a schema id. */
  inputSchemaDefinitionId: string;
  /** JSON text of the review view spec. Blank clears it. */
  viewText: string;
  /** User groups that see every task of this type. Empty keeps the queue personal. */
  viewerUserGroupIds: string[];
  /** Metric cards above the type's queue on a Task Page, in order. */
  metrics: TaskTypeMetricDraft[];
}

export function emptyEditorState(): TaskTypeEditorState {
  return {
    editing: null,
    key: '',
    name: '',
    description: '',
    anchorEntityType: NO_ANCHOR_VALUE,
    expectedSubtasks: [],
    completionMode: 'explicit',
    outcomeEnumId: NO_OUTCOME_ENUM_VALUE,
    outcomeFields: [],
    actions: [],
    procedures: [],
    taskNamespaceId: NO_NAMESPACE_VALUE,
    defaultAssigneeMode: 'none',
    defaultAssigneeUserId: '',
    defaultAssigneeAgentDefinitionId: '',
    defaultAssigneeUserGroupId: '',
    defaultAssigneeWorkflowId: '',
    defaultAssigneeAllocation: '',
    escalationTargetMode: 'none',
    escalationTargetUserId: '',
    escalationTargetUserGroupId: '',
    escalationTargetAllocation: '',
    inputSchemaDefinitionId: NO_INPUT_SCHEMA_VALUE,
    viewText: '',
    viewerUserGroupIds: [],
    metrics: metricDraftsFromStored(null),
  };
}

/** A type's saved default assignee as editor fields ('' = not picked). */
function defaultAssigneeEditorState(
  taskType: TaskTypeResponse,
): TaskTypeDefaultAssigneeEditorState {
  return {
    defaultAssigneeMode: taskType.defaultAssigneeType ?? 'none',
    defaultAssigneeUserId: taskType.defaultAssigneeUserId ?? '',
    defaultAssigneeAgentDefinitionId:
      taskType.defaultAssigneeAgentDefinitionId ?? '',
    defaultAssigneeUserGroupId: taskType.defaultAssigneeUserGroupId ?? '',
    defaultAssigneeWorkflowId: taskType.defaultAssigneeWorkflowId ?? '',
    defaultAssigneeAllocation: taskType.defaultAssigneeAllocation ?? '',
  };
}

/** A type's saved escalation target as editor fields ('' = not picked). */
function escalationTargetEditorState(
  taskType: TaskTypeResponse,
): TaskTypeEscalationTargetEditorState {
  return {
    escalationTargetMode: taskType.escalationTargetType ?? 'none',
    escalationTargetUserId: taskType.escalationTargetUserId ?? '',
    escalationTargetUserGroupId: taskType.escalationTargetUserGroupId ?? '',
    escalationTargetAllocation: taskType.escalationTargetAllocation ?? '',
  };
}

export function editorStateFor(
  taskType: TaskTypeResponse,
): TaskTypeEditorState {
  return {
    editing: taskType,
    key: taskType.key,
    name: taskType.name,
    description: taskType.description ?? '',
    anchorEntityType: taskType.anchorEntityType ?? NO_ANCHOR_VALUE,
    expectedSubtasks: taskType.expectedSubtasks ?? [],
    completionMode: taskType.completionMode,
    outcomeEnumId: taskType.outcomeEnumId ?? NO_OUTCOME_ENUM_VALUE,
    outcomeFields: taskType.outcomeFields ?? [],
    actions: actionDraftsFromStored(taskType.actions),
    procedures: procedureDraftsFromStored(taskType.procedures),
    taskNamespaceId: taskType.taskNamespaceId ?? NO_NAMESPACE_VALUE,
    ...defaultAssigneeEditorState(taskType),
    ...escalationTargetEditorState(taskType),
    inputSchemaDefinitionId:
      taskType.inputSchemaDefinitionId ?? NO_INPUT_SCHEMA_VALUE,
    viewText: viewTextFromStored(taskType.view),
    viewerUserGroupIds: [...taskType.viewerUserGroupIds],
    metrics: metricDraftsFromStored(taskType.metrics),
  };
}

/**
 * True when the default-assignee controls are in a saveable state: either no
 * default, or a kind with its id picked (and, for a team, its allocation). An
 * unpicked id is a half-edited form, not a clear — the server would reject it
 * anyway.
 */
export function isDefaultAssigneeComplete(
  editor: TaskTypeDefaultAssigneeEditorState,
): boolean {
  switch (editor.defaultAssigneeMode) {
    case 'none':
      return true;
    case 'user':
      return editor.defaultAssigneeUserId.length > 0;
    case 'agent':
      return editor.defaultAssigneeAgentDefinitionId.length > 0;
    case 'user_group':
      return (
        editor.defaultAssigneeUserGroupId.length > 0 &&
        editor.defaultAssigneeAllocation !== ''
      );
    case 'workflow':
      return editor.defaultAssigneeWorkflowId.length > 0;
  }
}

const NO_DEFAULT_ASSIGNEE_PAYLOAD: TaskTypeDefaultAssigneePayload = {
  defaultAssigneeType: null,
  defaultAssigneeUserId: null,
  defaultAssigneeAgentDefinitionId: null,
  defaultAssigneeUserGroupId: null,
  defaultAssigneeWorkflowId: null,
  defaultAssigneeAllocation: null,
};

/**
 * Wire payload for the default assignee. Switching kinds drops the other
 * kinds' ids (and the allocation unless it's a team) so the server-side
 * pairing rule holds; `none` clears the default.
 */
export function buildTaskTypeDefaultAssignee(
  editor: TaskTypeDefaultAssigneeEditorState,
): TaskTypeDefaultAssigneePayload {
  switch (editor.defaultAssigneeMode) {
    case 'user':
      return {
        ...NO_DEFAULT_ASSIGNEE_PAYLOAD,
        defaultAssigneeType: 'user',
        defaultAssigneeUserId: editor.defaultAssigneeUserId || null,
      };
    case 'agent':
      return {
        ...NO_DEFAULT_ASSIGNEE_PAYLOAD,
        defaultAssigneeType: 'agent',
        defaultAssigneeAgentDefinitionId:
          editor.defaultAssigneeAgentDefinitionId || null,
      };
    case 'user_group':
      return {
        ...NO_DEFAULT_ASSIGNEE_PAYLOAD,
        defaultAssigneeType: 'user_group',
        defaultAssigneeUserGroupId: editor.defaultAssigneeUserGroupId || null,
        defaultAssigneeAllocation: editor.defaultAssigneeAllocation || null,
      };
    case 'workflow':
      return {
        ...NO_DEFAULT_ASSIGNEE_PAYLOAD,
        defaultAssigneeType: 'workflow',
        defaultAssigneeWorkflowId: editor.defaultAssigneeWorkflowId || null,
      };
    case 'none':
      return NO_DEFAULT_ASSIGNEE_PAYLOAD;
  }
}

/**
 * True when the escalation-target controls are in a saveable state: either
 * no target, or a kind with its id picked (and, for a team, its allocation).
 */
export function isEscalationTargetComplete(
  editor: TaskTypeEscalationTargetEditorState,
): boolean {
  switch (editor.escalationTargetMode) {
    case 'none':
      return true;
    case 'user':
      return editor.escalationTargetUserId.length > 0;
    case 'user_group':
      return (
        editor.escalationTargetUserGroupId.length > 0 &&
        editor.escalationTargetAllocation !== ''
      );
  }
}

const NO_ESCALATION_TARGET_PAYLOAD: TaskTypeEscalationTargetPayload = {
  escalationTargetType: null,
  escalationTargetUserId: null,
  escalationTargetUserGroupId: null,
  escalationTargetAllocation: null,
};

/**
 * Wire payload for the escalation target. Switching kinds drops the other
 * kind's id (and the allocation unless it's a team) so the server-side
 * pairing rule holds; `none` clears the target.
 */
export function buildTaskTypeEscalationTarget(
  editor: TaskTypeEscalationTargetEditorState,
): TaskTypeEscalationTargetPayload {
  switch (editor.escalationTargetMode) {
    case 'user':
      return {
        ...NO_ESCALATION_TARGET_PAYLOAD,
        escalationTargetType: 'user',
        escalationTargetUserId: editor.escalationTargetUserId || null,
      };
    case 'user_group':
      return {
        ...NO_ESCALATION_TARGET_PAYLOAD,
        escalationTargetType: 'user_group',
        escalationTargetUserGroupId: editor.escalationTargetUserGroupId || null,
        escalationTargetAllocation: editor.escalationTargetAllocation || null,
      };
    case 'none':
      return NO_ESCALATION_TARGET_PAYLOAD;
  }
}

const EDITOR_SCALAR_KEYS = [
  'key',
  'name',
  'description',
  'anchorEntityType',
  'completionMode',
  'outcomeEnumId',
  'taskNamespaceId',
  'defaultAssigneeMode',
  'defaultAssigneeUserId',
  'defaultAssigneeAgentDefinitionId',
  'defaultAssigneeUserGroupId',
  'defaultAssigneeWorkflowId',
  'defaultAssigneeAllocation',
  'escalationTargetMode',
  'escalationTargetUserId',
  'escalationTargetUserGroupId',
  'escalationTargetAllocation',
  'inputSchemaDefinitionId',
  'viewText',
] as const;

function sameStringSet(
  left: readonly string[],
  right: readonly string[],
): boolean {
  if (left.length !== right.length) return false;
  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return sortedLeft.every((value, index) => value === sortedRight[index]);
}

function actionDraftsSignature(drafts: readonly TaskTypeActionDraft[]): string {
  return JSON.stringify(
    drafts.map(
      ({
        key,
        label,
        effectKind,
        effectTarget,
        outcomeStatus,
        outcome,
        outcomeReason,
        formFields,
      }) => ({
        key,
        label,
        effectKind,
        effectTarget,
        outcomeStatus,
        outcome,
        outcomeReason,
        formFields: formFields.map(
          ({
            key: fieldKey,
            label: fieldLabel,
            type,
            required,
            helpText,
            optionsText,
          }) => ({
            key: fieldKey,
            label: fieldLabel,
            type,
            required,
            helpText,
            optionsText,
          }),
        ),
      }),
    ),
  );
}

/** True when the form differs from an empty create or the loaded edit snapshot. */
export function isEditorDirty(editor: TaskTypeEditorState): boolean {
  const baseline = editor.editing
    ? editorStateFor(editor.editing)
    : emptyEditorState();
  const scalarChanged = EDITOR_SCALAR_KEYS.some(
    (key) => editor[key] !== baseline[key],
  );
  return (
    scalarChanged ||
    JSON.stringify(editor.outcomeFields) !==
      JSON.stringify(baseline.outcomeFields) ||
    JSON.stringify(editor.expectedSubtasks) !==
      JSON.stringify(baseline.expectedSubtasks) ||
    procedureDraftSignature(editor.procedures) !==
      procedureDraftSignature(baseline.procedures) ||
    actionDraftsSignature(editor.actions) !==
      actionDraftsSignature(baseline.actions) ||
    !sameStringSet(editor.viewerUserGroupIds, baseline.viewerUserGroupIds) ||
    metricDraftsSignature(editor.metrics) !==
      metricDraftsSignature(baseline.metrics)
  );
}

export function isSystemEntityValue(value: string): value is SystemEntity {
  const values: readonly string[] = Object.values(SystemEntity);
  return values.includes(value);
}

/** Wire payload for create/update behavior fields. */
export function buildTaskTypeBehavior(state: TaskTypeEditorState): {
  anchorEntityType: SystemEntity | null;
  expectedSubtasks: TaskExpectedSubtask[] | null;
  completionMode: TaskCompletionMode;
  outcomeEnumId: string | null;
  outcomeFields: TaskOutcomeField[] | null;
  actions: TaskTypeAction[] | null;
  procedures: TaskProcedureBinding[] | null;
  taskNamespaceId: string | null;
  inputSchemaDefinitionId: string | null;
  view: TaskTypeView | null;
  viewerUserGroupIds: string[];
  metrics: TaskTypeMetric[] | null;
} & TaskTypeDefaultAssigneePayload &
  TaskTypeEscalationTargetPayload {
  const actions = parseActionDrafts(state.actions);
  const procedures = parseProcedureDrafts(state.procedures);
  return {
    anchorEntityType: isSystemEntityValue(state.anchorEntityType)
      ? state.anchorEntityType
      : null,
    expectedSubtasks:
      state.expectedSubtasks.length > 0 ? state.expectedSubtasks : null,
    completionMode: state.completionMode,
    outcomeFields: state.outcomeFields.length
      ? TaskOutcomeFieldsSchema.parse(state.outcomeFields)
      : null,
    outcomeEnumId:
      state.outcomeEnumId === NO_OUTCOME_ENUM_VALUE
        ? null
        : state.outcomeEnumId,
    actions: actions.ok && actions.actions.length > 0 ? actions.actions : null,
    procedures:
      procedures.ok && procedures.procedures.length > 0
        ? procedures.procedures
        : null,
    taskNamespaceId:
      state.taskNamespaceId === NO_NAMESPACE_VALUE
        ? null
        : state.taskNamespaceId,
    inputSchemaDefinitionId:
      state.inputSchemaDefinitionId === NO_INPUT_SCHEMA_VALUE
        ? null
        : state.inputSchemaDefinitionId,
    view: viewForSave(state.viewText),
    ...buildTaskTypeDefaultAssignee(state),
    ...buildTaskTypeEscalationTarget(state),
    viewerUserGroupIds: state.viewerUserGroupIds,
    metrics: metricsForSave(state.metrics, state.editing?.metrics),
  };
}
